/**
 * Dev-only freeze "flight recorder".
 *
 * A frozen tab is a blocked main thread, and when it happens the user usually has
 * to kill the tab — losing all console logs. This persists rolling breadcrumbs
 * (heap usage, DOM size, main-thread stalls) to localStorage so the state right
 * BEFORE a freeze survives the kill. On the next load it logs the previous
 * session's last breadcrumb + any recorded freeze, which tells apart the likely
 * causes:
 *   - heap climbing toward the limit → a memory blow-up
 *   - a long stall with stable heap → a CPU loop / runaway synchronous work
 *
 * Off in production unless opted in: set localStorage `mol-freeze:enable` = '1'
 * and reload (see shouldRecordFreezes in main.tsx). Read the console after a
 * freeze+reload, or inspect localStorage keys `mol-freeze:last` /
 * `mol-freeze:lastFreeze`.
 *
 * @module
 */

import { redactUrl } from './redact-url.js'

const LAST_KEY = 'mol-freeze:last'
const FREEZE_KEY = 'mol-freeze:lastFreeze'
/** Liveness tick cadence — also the breadcrumb persistence cadence. */
const TICK_MS = 2000
/** A main-thread stall / long task beyond this is recorded as a freeze. */
const FREEZE_MS = 1500

interface HeapInfo {
  heapMB: number
  limitMB: number
}

/** Read the JS heap (Chrome/Brave only; undefined elsewhere). */
function heap(): HeapInfo | null {
  const mem = (
    performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }
  ).memory
  if (!mem) return null
  return {
    heapMB: Math.round(mem.usedJSHeapSize / 1048576),
    limitMB: Math.round(mem.jsHeapSizeLimit / 1048576),
  }
}

/**
 * Rolling ring of the most recent console lines. A freeze wedges the whole renderer
 * (Debugger.pause / Profiler.stop both time out — the hot loop is un-introspectable
 * live), so the only way to name the culprit is to persist WHAT was happening just
 * before the stall. console.* is synchronous, so the last line pushed here IS the last
 * thing that ran before the thread locked. Also counts lines/sec to expose a log storm.
 */
const RECENT_MAX = 20
const recentConsole: Array<{ t: number; type: string; msg: string }> = []
let consoleSinceTick = 0

function installConsoleTap(): void {
  const types = ['log', 'warn', 'error', 'info', 'debug'] as const
  for (const type of types) {
    const orig = console[type].bind(console)
    console[type] = (...args: unknown[]): void => {
      try {
        consoleSinceTick++
        recentConsole.push({
          t: Date.now(),
          type,
          msg: args
            .map((a) =>
              typeof a === 'string' ? a : ((a as { message?: string })?.message ?? String(a)),
            )
            .join(' ')
            .slice(0, 120),
        })
        if (recentConsole.length > RECENT_MAX) recentConsole.shift()
      } catch (_error) {
        /* the tap must never break logging — swallow its own errors */
      }
      orig(...args)
    }
  }
}

/**
 * Last page-lifecycle transition (visibilitychange / freeze / resume / pagehide).
 * A renderer observed hard-wedged in a synchronous IPC wait (2026-08-27) raised
 * the question of whether wedges begin while the tab is backgrounded/frozen —
 * this breadcrumb answers it from the recording alone.
 */
let lastLifecycle = { event: 'load', at: new Date().toISOString() }

function installLifecycleTap(): void {
  const record = (event: string) => (): void => {
    lastLifecycle = { event, at: new Date().toISOString() }
  }
  document.addEventListener('visibilitychange', () => {
    lastLifecycle = {
      event: 'visibility:' + document.visibilityState,
      at: new Date().toISOString(),
    }
  })
  // Page Lifecycle API — fired when the browser intentionally freezes/resumes
  // the page's task queues (battery/memory saver, background freezing).
  document.addEventListener('freeze', record('freeze'))
  document.addEventListener('resume', record('resume'))
  window.addEventListener('pagehide', record('pagehide'))
}

/** Capture a cheap snapshot of main-thread health. */
function snapshot(extra: Record<string, unknown> = {}): Record<string, unknown> {
  const snap: Record<string, unknown> = {
    at: new Date().toISOString(),
    ...(heap() ?? {}),
    domNodes: document.getElementsByTagName('*').length,
    url:
      typeof location !== 'undefined' ? redactUrl(location.pathname + location.search) : undefined,
    visibility: document.visibilityState,
    lastLifecycle,
    // recent activity BEFORE any stall — the breadcrumb that names the culprit
    consolePerTick: consoleSinceTick,
    recentConsole: recentConsole.slice(-RECENT_MAX),
    ...extra,
  }
  consoleSinceTick = 0
  return snap
}

function write(key: string, data: Record<string, unknown>): void {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch (_error) {
    /* quota / disabled — ignore; localStorage writes are best-effort */
  }
}

/** Log the previous session's recorder data once on load. */
function reportPrevious(): void {
  try {
    const freeze = localStorage.getItem(FREEZE_KEY)
    if (freeze) {
      console.warn(
        '[mol-freeze] PREVIOUS FREEZE recorded — heap/limit in MB. If heap≈limit it was a memory blow-up; a large stallMs/longTaskMs with stable heap is a CPU loop:',
        JSON.parse(freeze),
      )
    }
    const last = localStorage.getItem(LAST_KEY)
    if (last) {
      console.info('[mol-freeze] last breadcrumb before the previous reload:', JSON.parse(last))
    }
  } catch (_error) {
    /* ignore — localStorage may be disabled or JSON corrupted; diagnostic data is best-effort */
  }
}

/**
 * Start the freeze recorder. Idempotent-ish — intended to be called once at app
 * startup (dev only). Returns immediately; recording runs in the background.
 */
export function startFreezeRecorder(): void {
  installConsoleTap()
  installLifecycleTap()
  reportPrevious()

  // Liveness watchdog. The interval cannot fire while the thread is blocked, so a
  // tick arriving much later than TICK_MS means we were frozen in between. The
  // breadcrumb written each tick captures the state ~TICK_MS before any freeze,
  // which survives even if the tab is killed mid-freeze (the stall itself can't be
  // measured until the thread frees up).
  //
  // Hidden tabs are excluded: the browser throttles background timers to ~one
  // wake a minute, so every wake read as a 10–58s "stall" and the recorder cried
  // wolf once a minute for as long as the tab stayed backgrounded (observed
  // 2026-08-29 — heap flat, thread idle; pure throttling). A gap that overlaps a
  // hidden period is re-baselined away; only foreground-to-foreground gaps count.
  let lastTick = Date.now()
  let wasHidden = document.visibilityState === 'hidden'
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') wasHidden = true
  })
  setInterval(() => {
    const now = Date.now()
    if (document.visibilityState === 'hidden' || wasHidden) {
      // Throttled (or just returned from being throttled) — the gap is the
      // browser's scheduling, not a frozen thread. Re-baseline and record only
      // the breadcrumb.
      wasHidden = document.visibilityState === 'hidden'
      lastTick = now
      write(LAST_KEY, snapshot({ stallMs: 0 }))
      return
    }
    const stallMs = now - lastTick - TICK_MS
    lastTick = now
    write(LAST_KEY, snapshot({ stallMs: Math.max(0, Math.round(stallMs)) }))
    if (stallMs > FREEZE_MS) {
      const snap = snapshot({ kind: 'stall', stallMs: Math.round(stallMs) })
      write(FREEZE_KEY, snap)

      console.error(`[mol-freeze] main thread stalled ~${Math.round(stallMs)}ms`, snap)
    }
  }, TICK_MS)

  // Long-task observer — finer attribution for stalls that DO complete (a single
  // long synchronous task, vs. an infinite loop that never returns).
  try {
    const obs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.duration > FREEZE_MS) {
          const snap = snapshot({ kind: 'longtask', longTaskMs: Math.round(e.duration) })
          write(FREEZE_KEY, snap)

          console.error(`[mol-freeze] long task ${Math.round(e.duration)}ms`, snap)
        } else if (e.duration > 200) {
          console.warn(`[mol-freeze] long task ${Math.round(e.duration)}ms`)
        }
      }
    })
    obs.observe({ entryTypes: ['longtask'] })
  } catch (_error) {
    /* longtask PerformanceObserver unsupported in this browser — the watchdog interval still covers freezes */
  }
}
